import { useQuery } from '@apollo/client';
import { RemoveCircle } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import { useDeletePlaylistItem } from '~/api/playlist-items';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { LoadingPane } from '~/components/loading-pane';
import { PlaylistContainer } from '~/components/playlists/PlaylistContainer';
import { ShuffleButton } from '~/components/playlists/ShuffleButton';
import { AddContentButton } from '~/components/playlists/add-content/button';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { assert } from '~/lib/assert';
import { PlaylistDesignDocument } from '../queries/queries.generated';
import { BulkActionsMenu } from './components/bulk-actions-menu';
import { Layout } from './components/layout';
import { PlaylistProvider } from './context';

export const PlaylistDetailPage = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [confirmBulkItemsDelete, confirmBulkItemsDeleteProps] = useConfirmDialog();
  const [confirmSingleItemDelete, confirmSingleItemDeleteProps] = useConfirmDialog();

  const [deletePlaylistItem] = useDeletePlaylistItem();

  const params = useParams<{ playlistId: string; playlistFolderId: string }>();

  const playlistId = parseInt(params.playlistId ?? '');
  const playlistFolderId = Number(params.playlistFolderId);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data, loading } = useQuery(PlaylistDesignDocument, {
    variables: { networkId: currentNetwork.id, playlistId, playlistFolderId },
  });

  const playlist = data?.network?.playlist;
  const playlistFolder = data?.network?.playlistFolder;

  const link = useLink();

  const removeBulkPlaylistItems = useCallback(async () => {
    assert(playlist != null, 'No playlist');

    // Get out if no items are selected
    if (selectedIds.length === 0) return;

    if (!(await confirmBulkItemsDelete())) return;

    await deletePlaylistItem({
      variables: {
        playlistId: playlist.id,
        playlistItemIds: selectedIds,
      },
    });

    // Reset selected
    setSelectedIds([]);
  }, [confirmBulkItemsDelete, deletePlaylistItem, playlist, selectedIds]);

  // Helper to remove a single playlist item.
  const removeSinglePlaylistItem = useCallback(
    async (playlistItemId: number) => {
      assert(playlist != null, 'No playlist');

      if (!(await confirmSingleItemDelete())) return;

      await deletePlaylistItem({
        variables: {
          playlistId: playlist.id,
          playlistItemIds: [playlistItemId],
        },
      });
    },
    [confirmSingleItemDelete, deletePlaylistItem, playlist],
  );

  if (!loading && !data?.network?.playlist) return <Navigate to={link('/not-found')} replace />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {playlist && playlistFolder && (
        <PlaylistProvider playlist={playlist} playlistFolder={playlistFolder}>
          <Layout>
            <Paper>
              <Stack
                direction={{ sm: 'column', md: 'row' }}
                spacing={{ sm: 1, md: 2 }}
                alignItems={{ md: 'flex-start', lg: 'center' }}
                justifyContent="space-between"
                sx={{ p: 2 }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Typography variant="h4">
                      {t('components.playlist_detail_page.content_title')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {t('components.playlist_detail_page.content_description')}
                  </Typography>
                </Box>
              </Stack>
              <PlaylistContainer
                bulkMenu={<BulkActionsMenu onRemoveContent={removeBulkPlaylistItems} />}
                playlist={playlist}
                loading={loading}
                onRemoveContent={removeSinglePlaylistItem}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                toolbarActions={[
                  <ShuffleButton
                    key="shuffle"
                    playlistId={playlist.id}
                    enabled={playlist.playlistItems.length > 1}
                  />,
                  <AddContentButton key="add" playlistId={playlist.id} />,
                ]}
              />
              <ConfirmDialog
                {...confirmBulkItemsDeleteProps}
                confirm={t('common.dialogs.remove')}
                deleteConfirm
                deleteIcon={<RemoveCircle />}
                prompt={t('components.playlist_detail_page.remove_bulk_prompt')}
                title={t('components.playlist_detail_page.remove_content_title')}
              />
              <ConfirmDialog
                {...confirmSingleItemDeleteProps}
                confirm={t('common.dialogs.remove')}
                deleteConfirm
                deleteIcon={<RemoveCircle />}
                prompt={t('components.playlist_detail_page.remove_single_prompt')}
                title={t('components.playlist_detail_page.remove_content_title')}
              />
            </Paper>
          </Layout>
        </PlaylistProvider>
      )}
    </LoadingPane>
  );
};
